<template>
  <div width="100%">
    <date-range-picker
      v-model="localRange"
      time-picker-seconds
      time-picker
      show-dropdowns
      :range.sync="selectedRange"
      :locale-data="{ firstDay: 1, format }"
      :max-range="maxRange"
      :max-date="maxDate"
      :linked-calendars="false"
      :ranges="ranges"
      @cancel="onClickClose"
      @update="onClickApply"
    >
      <template #activator="{ on }">
        <slot
          :label="label"
          :text="inputText"
          :placeholder="placeholder"
          :on="on"
        >
          <v-text-field
            outlined
            dense
            readonly
            :label="label"
            :value="inputText"
            :placeholder="placeholder"
            v-bind="$attrs"
            :clearable="clearable && !permanent"
            v-on="on"
            @click:clear="clearFilter"
          >
            <template #label>
              <slot name="label" />
            </template>
          </v-text-field>
        </slot>
      </template>
    </date-range-picker>
  </div>
</template>

<script>
import DateRangePicker from "@/components/DateRangePicker/DateRangePicker";
import Constants from "@/utils/Const";
import { DateTime } from "luxon";
import { isEqual } from "lodash";
import { localeData, DefaultRanges } from "@/components/DateRangePicker/util";
export default {
  components: {
    DateRangePicker
  },
  model: {
    prop: "model",
    event: "changeFilter"
  },
  props: {
    model: { type: Object, default: null },
    label: { type: String, default: "" },
    items: { type: Array, default: () => [] },
    maxRange: { type: Number, default: 0 },
    maxDate: {
      type: [String, Date],
      default() {
        return null;
      }
    },
    format: { type: String, default: Constants.LuxonFullDateTimeFormat },
    placeholder: { type: String, default: null },
    hiddenRanges: { type: Array, default: () => ["currentYear"] },
    output: { type: String, default: null },
    inputFormatFn: { type: Function, default: null },
    outputFormatFn: { type: Function, default: null },
    clearable: { type: Boolean, default: true },
    permanent: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      localRange: {},
      selectedRange: null,
      ranges: DefaultRanges
    };
  },
  computed: {
    range: {
      get() {
        return this.model;
      },
      set(v) {
        this.$emit("changeFilter", v);
      }
    },
    inputText() {
      const { startDate, endDate } = this.parseRange(this.range, true);
      if (!startDate || !endDate) return null;
      if (typeof this.inputFormatFn === "function") {
        return this.inputFormatFn(this.range, this.label);
      } else {
        return (
          startDate.toFormat(Constants.LuxonFullDateTimeFormat) +
          " - " +
          endDate.toFormat(Constants.LuxonFullDateTimeFormat)
        );
      }
    }
  },
  watch: {
    range(newV, oldV) {
      if (!isEqual(newV, oldV)) this.onChangeRange(newV);
    }
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      this.onChangeRange(this.range);
      this.$emit("init");
    },
    onClickInput() {
      this.open = true;
    },
    onChangeRange(inputRange) {
      const range = this.parseRange(inputRange);
      this.setLocalRange(range);
      this.setOutput(this.getOutput());
      if (!range.endDate || !range.startDate) {
        this.clearFilter();
      }
    },
    onClickClose() {
      this.localRange = this.parseRange(this.range);
      this.closeFilter();
    },
    onClickApply() {
      this.setRange(this.localRange);
      this.closeFilter();
      this.$emit("apply");
    },
    closeFilter() {
      this.open = false;
      this.$emit("close-filter");
    },
    setRange(range) {
      const { selectedRange } = this;
      this.range = range ? { ...range, interval: selectedRange } : range;
    },
    setLocalRange(range) {
      this.localRange = range;
    },
    setOutput(output) {
      this.$emit("update:output", output);
    },
    rangeFactory(startDate = null, endDate = null) {
      return { startDate, endDate };
    },
    clearFilter() {
      this.setRange(null);
      this.$emit("click:clear");
    },
    parseRange(range, toDatetime = false) {
      if (!range) return this.rangeFactory();
      const { endDate, startDate } = range;
      let endDt, startDt;
      if (range.interval && this.ranges[range.interval]) {
        const { start, end } = this.ranges[range.interval]();
        startDt = start;
        endDt = end;
        this.selectedRange = range.interval;
      } else {
        if (!endDate || !startDate) return this.rangeFactory();

        this.selectedRange = null;
        endDt = DateTime.fromISO(endDate);
        startDt = DateTime.fromISO(startDate);
      }

      if (!endDt.isValid || !startDt.isValid) return this.rangeFactory();

      if (toDatetime) {
        return this.rangeFactory(startDt, endDt);
      } else {
        return this.rangeFactory(startDt.toISO(), endDt.toISO());
      }
    },
    getOutput() {
      const { label, range, selectedRange } = this;
      const { startDate, endDate } = this.parseRange(range, true);
      if (!startDate || !endDate) return null;
      let output = label ? `${label}: ` : "";
      if (typeof this.outputFormatFn === "function") {
        return this.outputFormatFn(range, label, selectedRange);
      } else {
        const format = Constants.LuxonShortDateTimeFormat;
        if (selectedRange) {
          const locale = localeData({}, this.$i18n);
          output += `${locale.interval}: ${locale.ranges[
            selectedRange
          ].toLowerCase()}`;
        } else if (startDate.hasSame(endDate, "day")) {
          output += startDate.toFormat(Constants.LuxonDateFormat);
        } else if (
          startDate.diff(startDate.startOf("day")).milliseconds === 0 &&
          endDate.endOf("day").diff(endDate).milliseconds <= 999
        ) {
          output += `${startDate.toFormat(
            Constants.LuxonDateFormat
          )} - ${endDate.toFormat(Constants.LuxonDateFormat)}`;
        } else {
          output += `${startDate.toFormat(format)} - ${endDate.toFormat(
            format
          )}`;
        }
      }

      return output;
    }
  }
};
</script>
